import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/API';
import { useSnackbar } from 'notistack';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const token = localStorage.getItem('pharmacy_token');
    const userData = localStorage.getItem('pharmacy_user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { user, token } = response.data.data;
      
      localStorage.setItem('pharmacy_token', token);
      localStorage.setItem('pharmacy_user', JSON.stringify(user));
      setUser(user);
      
      enqueueSnackbar('Login successful!', { variant: 'success' });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('pharmacy_token');
    localStorage.removeItem('pharmacy_user');
    setUser(null);
    enqueueSnackbar('Logged out successfully', { variant: 'info' });
  };

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      const updatedUser = response.data.data.user;
      
      localStorage.setItem('pharmacy_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      enqueueSnackbar('Profile updated successfully!', { variant: 'success' });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    }
  };

  // Role permission definitions
  const rolePermissions = {
    inventory_clerk: {
      items: ['read', 'create', 'update'],
      departments: ['read'],
      itemIssues: ['read', 'create'],
      itemIssueReturns: ['read', 'create'],
      departmentStock: ['read']
    },
    inventory_manager: {
      items: ['read', 'create', 'update', 'delete'],
      departments: ['read', 'create', 'update'],
      itemIssues: ['read', 'create', 'approve', 'cancel'],
      itemIssueReturns: ['read', 'create', 'approve', 'reject', 'complete'],
      departmentStock: ['read', 'update']
    },
    department_manager: {
      items: ['read'],
      departments: ['read'],
      itemIssues: ['read', 'create'],
      itemIssueReturns: ['read', 'create'],
      departmentStock: ['read']
    },
    admin: {
      items: ['read', 'create', 'update', 'delete'],
      departments: ['read', 'create', 'update', 'delete'],
      itemIssues: ['read', 'create', 'approve', 'cancel', 'delete'],
      itemIssueReturns: ['read', 'create', 'approve', 'reject', 'complete', 'delete'],
      departmentStock: ['read', 'update', 'delete']
    }
  };

  // Check if user has a specific permission for a resource
  const hasPermission = (resource, action) => {
    if (!user) return false;
    
    const userRole = user.role;
    const permissions = rolePermissions[userRole];
    
    if (!permissions) return false;
    
    // Admin has all permissions
    if (userRole === 'admin') return true;
    
    // Check specific resource permissions
    if (permissions[resource]) {
      return permissions[resource].includes(action);
    }
    
    return false;
  };

  // Check if user can approve (for item issues and returns)
  const canApprove = () => {
    return hasPermission('itemIssues', 'approve') || 
           hasPermission('itemIssueReturns', 'approve');
  };

  // Check if user is department manager
  const isDepartmentManager = () => {
    return user?.role === 'department_manager';
  };

  // Check if user is inventory clerk
  const isInventoryClerk = () => {
    return user?.role === 'inventory_clerk';
  };

  // Check if user is inventory manager
  const isInventoryManager = () => {
    return user?.role === 'inventory_manager';
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const value = {
    user,
    login,
    logout,
    updateProfile,
    loading,
    isAuthenticated: !!user,
    hasRole: (role) => user?.role === role,
    hasAnyRole: (roles) => roles.includes(user?.role),
    hasPermission,
    canApprove,
    isDepartmentManager,
    isInventoryClerk,
    isInventoryManager,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};